import React, { useRef } from 'react';
import { Article } from '../types';
import { Calendar, User, Eye, ArrowLeft, ArrowRight } from 'lucide-react';
import { motion, AnimatePresence } from 'motion/react';
import { cn, formatDate } from '../lib/utils';
import { WRITERS } from '../constants';
import { useTranslate } from '../context/LanguageContext';

const getWriterAvatar = (article: Article, size = 100) => {
  if (article?.author_avatar) return article.author_avatar;
  const authorName = article?.author;
  const writer = WRITERS.find(w => w.name === authorName || w.name.replace('ي', 'ى') === authorName?.replace('ي', 'ى'));
  if (writer) return writer.avatar;
  return WRITERS[0]?.avatar || '/src/assets/images/author_hero_image_1780432828583.png';
};

interface SectionProps {
  title: string;
  articles: Article[];
  variant?: 'grid' | 'cards' | 'list' | 'slider' | 'opinion' | 'small' | 'pens' | 'interviews' | 'carousel' | 'intellectual' | 'dialogue-fade';
  fullWidth?: boolean;
  href?: string;
  isLoading?: boolean;
}

export default function Section({ title, articles, variant = 'grid', fullWidth = false, href, isLoading = false }: SectionProps) {
  const { language, t } = useTranslate();
  const isGrid = variant === 'grid';
  const isSlider = variant === 'slider';
  const isCarousel = variant === 'carousel';
  const isOpinion = variant === 'opinion';
  const isSmall = variant === 'small';
  const isPens = variant === 'pens';
  const isInterview = variant === 'interviews';
  const isIntellectual = variant === 'intellectual';
  const isDialogueFade = variant === 'dialogue-fade';
  const scrollRef = useRef<HTMLDivElement>(null);
  
  const isLatestArticles = title === 'أحدث مقالات' || title === 'أحدث المقالات' || title === 'Latest Articles' || title === t('أحدث مقالات');
  
  const [activeIndex, setActiveIndex] = React.useState(0);

  React.useEffect(() => {
    if (isDialogueFade && articles.length > 0) {
      const timer = setInterval(() => {
        setActiveIndex((prev) => (prev + 1) % articles.length);
      }, 5000);
      return () => clearInterval(timer);
    }
  }, [isDialogueFade, articles.length]);

  const scroll = (direction: 'left' | 'right') => {
    if (scrollRef.current) {
      const { scrollLeft, clientWidth } = scrollRef.current;
      const scrollTo = direction === 'left' ? scrollLeft - clientWidth : scrollLeft + clientWidth;
      scrollRef.current.scrollTo({ left: scrollTo, behavior: 'smooth' });
    }
  };
  
  return (
    <section className={cn(
      "py-12 md:py-24 relative overflow-hidden transition-colors duration-500",
      isIntellectual ? "bg-zinc-950 text-white" : 
      isOpinion ? "bg-zinc-50 dark:bg-zinc-950/50 border-y border-zinc-100 dark:border-zinc-900" : (isGrid ? "bg-zinc-50 dark:bg-zinc-900/10" : (fullWidth ? "w-full bg-white dark:bg-zinc-950" : "container mx-auto px-4"))
    )} dir={language === 'ar' ? 'rtl' : 'ltr'}>
      {(isOpinion || isPens || isIntellectual) && (
        <div className={cn(
          "absolute inset-0 pointer-events-none transition-opacity duration-500",
          isIntellectual ? "opacity-5" :
          isPens ? "opacity-[0.03] dark:opacity-[0.08]" : "opacity-[0.03] dark:opacity-[0.06]"
        )} 
             style={{ backgroundImage: isIntellectual ? 'linear-gradient(rgba(255,255,255,.1) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,.1) 1px, transparent 1px)' : 'radial-gradient(circle at 2px 2px, currentColor 1px, transparent 0)', backgroundSize: isIntellectual ? '40px 40px' : '32px 32px' }} 
        />
      )}
      <div className={cn(
        "mb-10 md:mb-16 flex flex-col md:flex-row md:items-end justify-between gap-6 relative z-10",
        (isGrid || fullWidth || isOpinion || isPens || isIntellectual) && "container mx-auto px-4 lg:px-12",
        language === 'ar' ? "text-right" : "text-left"
      )}>
        <div className="flex flex-col gap-3">
          <motion.div 
            initial={{ width: 0 }}
            whileInView={{ width: 64 }}
            className={cn("h-1", isIntellectual ? "bg-white" : "bg-secondary")} 
          />
          <h2 className={cn("text-2xl sm:text-3.5xl font-black relative leading-none transition-all duration-300 hover:text-secondary cursor-pointer group w-fit pb-2 tracking-tight", isIntellectual ? "text-white hover:text-white/80" : "text-primary dark:text-white hover:text-secondary")}>
            {t(title)}
            <span className={cn("absolute bottom-0 right-0 w-0 h-[3px] transition-all duration-300 group-hover:w-full", isIntellectual ? "bg-white" : "bg-secondary")} />
          </h2>
          <p className={cn("text-[12px] font-normal max-w-md transition-colors", isIntellectual ? "text-zinc-400" : "text-zinc-500 dark:text-zinc-400")}>
            {t('تغطية شاملة ومعمقة تتجاوز الخبر لتصل إلى جوهر القضية السودانية.')}
          </p>
        </div>
        
        <div className={cn("flex items-center gap-4", language === 'ar' ? "mr-auto" : "ml-auto")}>
          {(isSlider || isSmall || isCarousel) && (
            <div className="flex items-center gap-2">
              <button 
                onClick={() => scroll(language === 'ar' ? 'right' : 'left')}
                className="w-12 h-12 flex items-center justify-center rounded-full bg-white dark:bg-zinc-900 border border-zinc-100 dark:border-zinc-800 text-primary dark:text-white hover:bg-secondary hover:text-white dark:hover:bg-secondary transition-all shadow-lg hover:shadow-secondary/20"
              >
                <ArrowRight size={20} />
              </button>
              <button 
                onClick={() => scroll(language === 'ar' ? 'left' : 'right')}
                className="w-12 h-12 flex items-center justify-center rounded-full bg-white dark:bg-zinc-900 border border-zinc-100 dark:border-zinc-800 text-primary dark:text-white hover:bg-secondary hover:text-white dark:hover:bg-secondary transition-all shadow-lg hover:shadow-secondary/20"
              >
                <ArrowLeft size={20} />
              </button>
            </div>
          )}
          <a href={href || "/articles"} className="hidden sm:flex items-center gap-2 bg-primary text-white px-8 py-3.5 text-xs font-black uppercase tracking-widest hover:bg-secondary transition-all shadow-xl shadow-primary/10">
            {t('عرض الكل')} {language === 'ar' ? <ArrowLeft size={16} /> : <ArrowRight size={16} />}
          </a>
        </div>
      </div>

      {isLoading ? (
        <div className={cn(
          "grid relative z-10",
          (isGrid || isOpinion || isPens || isInterview || isIntellectual || fullWidth) ? "container mx-auto px-4 lg:px-12" : "",
          isIntellectual ? "grid-cols-1 lg:grid-cols-12 gap-12" :
          isInterview ? "grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8" :
          isPens ? "grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-x-8 gap-y-24 pt-10" :
          isGrid ? "grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-8 lg:gap-12" : 
          variant === 'opinion' ? "grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 lg:gap-12" :
          variant === 'cards' ? "grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 lg:gap-12" : "grid-cols-1 gap-8"
        )}>
          {isIntellectual ? (
            <>
              <div className="lg:col-span-8 animate-pulse bg-zinc-900/60 rounded-[2.5rem] aspect-video border border-white/5 p-8 flex flex-col justify-end space-y-4">
                <div className="h-4 bg-zinc-850 rounded w-1/4" />
                <div className="h-8 bg-zinc-850 rounded w-3/4" />
                <div className="h-4 bg-zinc-850 rounded w-1/2" />
              </div>
              <div className="lg:col-span-4 flex flex-col gap-10">
                {Array.from({ length: 3 }).map((_, i) => (
                  <div key={i} className="animate-pulse flex items-center gap-4 bg-zinc-900/40 border border-white/5 p-4 rounded-2xl">
                    <div className="w-24 h-24 bg-zinc-850 rounded-xl shrink-0" />
                    <div className="flex-1 space-y-3">
                      <div className="h-4 bg-zinc-850 rounded w-5/6" />
                      <div className="h-3 bg-zinc-850 rounded w-1/2" />
                    </div>
                  </div>
                ))}
              </div>
            </>
          ) : isPens ? (
            Array.from({ length: 4 }).map((_, i) => (
              <div key={i} className="animate-pulse bg-white dark:bg-zinc-950 border border-zinc-100 dark:border-zinc-900 p-8 rounded-[2.5rem] flex flex-col items-center text-center space-y-4">
                <div className="w-20 h-20 rounded-full bg-zinc-200 dark:bg-zinc-900/60" />
                <div className="h-4 bg-zinc-200 dark:bg-zinc-900/60 rounded w-1/2" />
                <div className="h-3 bg-zinc-200 dark:bg-zinc-900/60 rounded w-5/6" />
                <div className="h-3 bg-zinc-200 dark:bg-zinc-905/60 rounded w-2/3" />
              </div>
            ))
          ) : isInterview ? (
            Array.from({ length: 4 }).map((_, i) => (
              <div key={i} className="animate-pulse bg-white dark:bg-zinc-950 border border-zinc-100 dark:border-zinc-900 p-6 rounded-[2rem] space-y-4">
                <div className="aspect-video bg-zinc-200 dark:bg-zinc-900/60 rounded-[1.5rem]" />
                <div className="h-4 bg-zinc-200 dark:bg-zinc-900/60 rounded w-2/3" />
                <div className="h-3 bg-zinc-200 dark:bg-zinc-900/60 rounded w-1/2" />
              </div>
            ))
          ) : (
            Array.from({ length: 4 }).map((_, i) => (
              <div key={i} className="animate-pulse bg-white dark:bg-zinc-950 border border-zinc-100 dark:border-zinc-900 p-6 rounded-[2rem] space-y-4">
                <div className="aspect-[16/10] bg-zinc-200 dark:bg-zinc-900/60 rounded-[1.5rem]" />
                <div className="h-4 bg-zinc-200 dark:bg-zinc-900/60 rounded w-3/4" />
                <div className="h-3 bg-zinc-200 dark:bg-zinc-900/60 rounded w-1/2" />
              </div>
            ))
          )}
        </div>
      ) : isDialogueFade ? (
        <div className={cn(
          "relative min-h-[500px]",
          fullWidth ? "w-full px-4 lg:px-24" : "container mx-auto px-4 lg:px-12"
        )}>
          <AnimatePresence mode="wait">
            <motion.div
              key={articles[activeIndex]?.id}
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -20 }}
              transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
              className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-24 items-center"
            >
              <div className="relative aspect-video lg:aspect-[16/9] overflow-hidden rounded-[2.5rem] shadow-3xl">
                  <img 
                    src={articles[activeIndex]?.imageUrl} 
                    alt={t(articles[activeIndex]?.title)}
                    className="w-full h-full object-cover transition-transform duration-[5s] hover:scale-110"
                  />
                 <div className="absolute inset-0 bg-gradient-to-t from-zinc-950 via-zinc-950/20 to-transparent opacity-60" />
                 <div className={cn("absolute top-8", language === 'ar' ? "right-8" : "left-8")}>
                    <span className="bg-secondary text-zinc-950 text-[10px] font-black px-4 py-2 rounded-full shadow-xl uppercase tracking-widest">
                      {t('مقابلة حصرية')}
                    </span>
                 </div>
              </div>

              <div className={cn("space-y-8", language === 'ar' ? "text-right" : "text-left")}>
                 <div className={cn("flex items-center gap-6 text-zinc-400 text-[10px] font-bold uppercase tracking-[0.2em]", language === 'ar' ? "justify-start" : "justify-start")}>
                    <span className="flex items-center gap-2">
                       <Calendar size={14} className="text-secondary" />
                       {formatDate(articles[activeIndex]?.publishedDate)}
                    </span>
                    <span className="w-1.5 h-1.5 bg-zinc-200 dark:bg-zinc-800 rounded-full" />
                    <a href={`/author/${articles[activeIndex]?.author}`} className="flex items-center gap-3 group/author">
                       <div className="w-10 h-10 rounded-full overflow-hidden border-2 border-secondary/50 shadow-lg group-hover/author:border-secondary group-hover/author:scale-110 transition-all">
                          <img src={getWriterAvatar(articles[activeIndex], 100)} alt={t(articles[activeIndex]?.author)} className="w-full h-full object-cover" />
                       </div>
                       <span className="group-hover/author:text-secondary transition-colors text-sm font-black">
                         {t(articles[activeIndex]?.author)}
                       </span>
                    </a>
                 </div>

                 <h3 className="text-3xl lg:text-5xl font-black text-zinc-950 dark:text-white leading-[1.2] italic">
                    "{t(articles[activeIndex]?.title)}"
                 </h3>

                 <p className="text-lg text-zinc-500 dark:text-zinc-400 font-normal leading-relaxed line-clamp-3">
                    {t(articles[activeIndex]?.summary)}
                 </p>

                 <div className="pt-6">
                    <a 
                      href={`/article/${articles[activeIndex]?.id}`}
                      className="inline-flex items-center gap-6 group/btn"
                    >
                       <span className="text-xs font-black text-zinc-950 dark:text-white uppercase tracking-[0.4em]">
                         {t('اقرأ الحوار الكامل')}
                       </span>
                       <div className={cn("w-14 h-14 rounded-full border border-zinc-100 dark:border-zinc-800 flex items-center justify-center group-hover/btn:bg-secondary group-hover/btn:border-secondary group-hover/btn:text-white transition-all transform", language === 'ar' ? "group-hover/btn:-translate-x-2" : "group-hover/btn:translate-x-2")}>
                          {language === 'ar' ? <ArrowLeft size={20} /> : <ArrowRight size={20} />}
                       </div>
                    </a>
                 </div>
              </div>
            </motion.div>
          </AnimatePresence>

          <div className="flex justify-center lg:justify-start gap-3 mt-16">
              {articles.map((_, idx) => (
                <button 
                  key={idx}
                  onClick={() => setActiveIndex(idx)}
                  className={cn(
                    "h-1.5 transition-all duration-500 rounded-full",
                    activeIndex === idx ? "w-12 bg-secondary" : "w-4 bg-zinc-200 dark:bg-zinc-800 hover:bg-zinc-300"
                  )}
                />
              ))}
          </div>
        </div>
      ) : (isSlider || isSmall || isCarousel) ? (
        <div 
          ref={scrollRef}
          className={cn(
            "flex gap-8 overflow-x-auto snap-x snap-mandatory scrollbar-hide pb-12 pt-4 px-4",
            (fullWidth || isOpinion || isCarousel) && "lg:px-12"
          )}
          style={{ scrollbarWidth: 'none' }}
        >
          {articles.map((article, i) => (
            <motion.div
              key={article.id}
              initial={{ opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: i * 0.1, duration: 0.7, ease: "easeOut" }}
              className={cn(
                "flex-shrink-0 snap-start",
                isSmall ? "w-[260px] md:w-[300px]" : 
                isInterview ? "w-[300px] md:w-[500px] lg:w-[600px]" :
                isCarousel ? "w-full sm:w-[50%] lg:w-[33.333%] xl:w-[25%] pr-4" :
                "w-[280px] md:w-[320px] lg:w-[350px]"
              )}
            >
              {isInterview ? (
                <InterviewCard article={article} />
              ) : (
                <ArticleCard article={article} variant={variant} isLatestArticles={isLatestArticles} />
              )}
            </motion.div>
          ))}
        </div>
      ) : (
        <div className={cn(
          "grid relative z-10",
          (isGrid || isOpinion || isPens || isInterview || isIntellectual || fullWidth) ? "container mx-auto px-4 lg:px-12" : "",
          isIntellectual ? "grid-cols-1 lg:grid-cols-12 gap-12" :
          isInterview ? "grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8" :
          isPens ? "grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-x-8 gap-y-24 pt-10" :
          isGrid ? "grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-8 lg:gap-12" : 
          variant === 'opinion' ? "grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 lg:gap-12" :
          variant === 'cards' ? "grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 lg:gap-12" : "grid-cols-1 gap-8"
        )}>
        {isIntellectual ? (
          <>
            <motion.div 
              className="lg:col-span-8 group"
              initial={{ opacity: 0, x: 50 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 1, ease: [0.16, 1, 0.3, 1] }}
            >
              <IntellectualCard article={articles[0]} featured={true} />
            </motion.div>
            
            <div className="lg:col-span-4 flex flex-col gap-10">
              {articles.slice(1, 4).map((article, i) => (
                <motion.div
                  key={article.id}
                  initial={{ opacity: 0, x: -50 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: 0.2 + (i * 0.15), duration: 0.8 }}
                >
                  <IntellectualCard article={article} />
                </motion.div>
              ))}
            </div>
          </>
        ) : (
          articles.map((article, i) => (
            <motion.div 
              key={article.id}
              initial={{ opacity: 0, y: isPens ? 60 : 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ 
                delay: i * 0.1, 
                duration: 0.8,
                ease: [0.16, 1, 0.3, 1]
              }}
            >
              {variant === 'opinion' ? (
                <OpinionCard article={article} />
              ) : variant === 'pens' ? (
                <PenCard article={article} />
              ) : variant === 'interviews' ? (
                <InterviewCard article={article} />
              ) : (
                <ArticleCard article={article} variant={variant} isLatestArticles={isLatestArticles} />
              )}
            </motion.div>
          ))
        )}
        </div>
      )}
    </section>
  );
}

function IntellectualCard({ article, featured = false }: { article: Article, featured?: boolean }) {
  const { language, t } = useTranslate();
  if (!article) return null;
  return (
    <article className={cn(
      "group relative overflow-hidden transition-all duration-700",
      featured ? "rounded-none" : "flex gap-6 items-start",
      language === 'ar' ? "text-right" : "text-left"
    )}>
      {featured ? (
        <div className="relative aspect-[16/10] lg:aspect-[16/9] overflow-hidden">
          <img 
            src={article.imageUrl} 
            alt={t(article.title)} 
            className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-2000"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-zinc-950 via-zinc-950/40 to-transparent" />
          
          <div className="absolute inset-0 p-8 lg:p-16 flex flex-col justify-end">
            <a 
              href={`/category/${article.category === 'مقالات الرأي' ? 'opinion' : article.category === 'أقلام حرة' ? 'free-pens' : article.category === 'قضايا فكرية' ? 'intellectual' : article.category === 'حوارات' ? 'dialogues' : 'news'}`}
              className={cn("flex items-center gap-3 text-secondary text-xs font-black uppercase tracking-[0.3em] mb-6 hover:text-white transition-colors", language === 'ar' ? "flex-row-reverse" : "flex-row")}
            >
              <span className="w-12 h-px bg-secondary" />
              {t('رؤية فكرية')}
            </a>
            
            <h3 className="text-[14px] font-normal text-white leading-relaxed mb-6 max-w-4xl group-hover:text-secondary transition-colors duration-500">
              <a href={`/article/${article.slug || article.id}`}>{t(article.title)}</a>
            </h3>
            
            <p className="text-zinc-400 text-[12px] leading-loose max-w-3xl line-clamp-2 font-normal not-italic mb-10 opacity-0 group-hover:opacity-100 transform translate-y-4 group-hover:translate-y-0 transition-all duration-700">
              {t(article.summary)}
            </p>
            
            <div className={cn("flex items-center gap-8", language === 'ar' ? "flex-row" : "flex-row")}>
              <div className="flex items-center gap-4">
                <a href={`/author/${article.author}`} className="w-16 h-16 rounded-full border-2 border-white/20 p-1 block group/avatar shadow-xl hover:shadow-secondary/20 transition-all">
                  <img src={getWriterAvatar(article, 150)} alt={t(article.author)} referrerPolicy="no-referrer" className="w-full h-full object-cover rounded-full group-hover/avatar:scale-110 transition-transform" />
                </a>
                <div className="flex flex-col">
                  <a href={`/author/${article.author}`} className="text-white font-black text-sm hover:text-secondary transition-colors">{t(article.author)}</a>
                  <span className="text-zinc-500 text-[10px] uppercase tracking-widest font-bold">
                    {t('باحث فكري')}
                  </span>
                </div>
              </div>
              <div className="h-10 w-px bg-white/10" />
              <div className="text-zinc-400 text-xs font-bold font-mono">
                {formatDate(article.publishedDate)}
              </div>
            </div>
          </div>
        </div>
      ) : (
        <>
          <div className="w-32 h-32 lg:w-40 lg:h-40 shrink-0 overflow-hidden rounded-none group-hover:rounded-none transition-all duration-700">
            <img 
              src={article.imageUrl} 
              alt={t(article.title)} 
              className="w-full h-full object-cover group-hover:scale-125 transition-transform duration-1000 grayscale group-hover:grayscale-0"
            />
          </div>
          <div className="flex flex-col py-2">
            <div className="text-[10px] font-normal text-secondary uppercase tracking-widest mb-3 opacity-60">
              {t('دراسات معمقة')}
            </div>
            <h3 className="text-[14px] font-normal text-white group-hover:text-secondary transition-colors leading-[1.4] line-clamp-2 mb-4">
              <a href={`/article/${article.slug || article.id}`}>{t(article.title)}</a>
            </h3>
            <div className="flex items-center gap-3 text-zinc-500 text-[10px] font-bold mt-auto">
               <User size={12} className="text-zinc-700" />
               {t(article.author)}
               <span className="w-1 h-1 bg-zinc-800 rounded-full mx-1" />
               <Calendar size={12} className="text-zinc-700" />
               {formatDate(article.publishedDate)}
            </div>
          </div>
        </>
      )}
    </article>
  );
}

function InterviewCard({ article }: { article: Article }) {
  const { language, t } = useTranslate();
  return (
    <article className={cn("group flex flex-col h-full bg-white dark:bg-zinc-900 border border-zinc-100 dark:border-zinc-800 transition-all duration-500 hover:shadow-2xl hover:shadow-primary/10 overflow-hidden relative rounded-2xl", language === 'ar' ? "text-right" : "text-left")}>
      <div className={cn("absolute top-4", language === 'ar' ? "left-4" : "right-4")}>
        <div className="bg-secondary text-zinc-950 text-[8px] font-black px-2.5 py-1 uppercase tracking-[0.2em] rounded-full shadow-lg">
          {t('حوار حصري')}
        </div>
      </div>

      <div className="w-full aspect-video relative overflow-hidden bg-zinc-200 dark:bg-zinc-800 shrink-0">
        <img 
          src={article.imageUrl} 
          alt={t(article.title)} 
          className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-1000"
        />
        <div className="absolute inset-x-0 bottom-0 h-1/2 bg-gradient-to-t from-primary/90 via-primary/50 to-transparent flex items-end p-5" />
      </div>
      
      <div className="w-full p-6 flex flex-col justify-center relative bg-primary transition-colors duration-500">
        <div className="flex items-center gap-2 text-secondary text-[10px] font-black uppercase tracking-wider mb-4">
          <Calendar size={12} />
          {formatDate(article.publishedDate)}
        </div>
        
        <h3 className="text-[14px] font-normal text-white group-hover:text-secondary transition-colors leading-[1.3] mb-4 line-clamp-2 min-h-[3rem]">
          <a href={`/article/${article.slug || article.id}`}>{t(article.title)}</a>
        </h3>

        <p className="text-zinc-300 text-[12px] leading-relaxed line-clamp-2 font-normal not-italic mb-8 opacity-90 transition-colors">
          {t(article.summary)}
        </p>

        <div className="flex items-center justify-between border-t border-white/10 pt-6 mt-auto">
          <a href={`/author/${article.author}`} className="flex items-center gap-3 group/author">
             <div className="w-14 h-14 rounded-full border-2 border-white/10 overflow-hidden shadow-lg group-hover/author:border-secondary/50 transition-all">
                <img src={getWriterAvatar(article, 150)} alt={t(article.author)} referrerPolicy="no-referrer" className="w-full h-full object-cover group-hover/author:scale-110 transition-transform" />
             </div>
             <div className="flex flex-col text-right">
                <span className="text-[11px] font-black text-white group-hover/author:text-secondary transition-colors">{t(article.author)}</span>
                <span className="text-[9px] text-zinc-400 font-bold uppercase tracking-widest transition-colors">
                  {t('محاور الموقع')}
                </span>
             </div>
          </a>
          <div className={cn("w-10 h-10 rounded-full bg-white/10 flex items-center justify-center text-white group-hover:bg-secondary group-hover:text-white transition-all transform border border-white/5", language === 'ar' ? "group-hover:-translate-x-2" : "group-hover:translate-x-2")}>
             {language === 'ar' ? <ArrowLeft size={18} /> : <ArrowRight size={18} />}
          </div>
        </div>
      </div>
    </article>
  );
}

function PenCard({ article }: { article: Article }) {
  const { language, t } = useTranslate();
  return (
    <article className="group relative bg-white dark:bg-zinc-900 border border-zinc-100 dark:border-zinc-800 pt-16 pb-8 px-8 h-full flex flex-col transition-all duration-500 hover:bg-black dark:hover:bg-black hover:border-black dark:hover:border-black hover:shadow-2xl hover:shadow-primary/5 rounded-2xl">
      <div className="absolute -top-12 left-1/2 -translate-x-1/2 w-24 h-24 rounded-full border-4 border-white dark:border-zinc-900 group-hover:border-black dark:group-hover:border-black shadow-2xl overflow-hidden group-hover:scale-110 transition-transform duration-500">
        <img 
          src={getWriterAvatar(article, 150)} 
          alt={t(article.author)} 
          referrerPolicy="no-referrer"
          className="w-full h-full object-cover grayscale group-hover:grayscale-0 transition-all duration-700" 
        />
      </div>

      <div className="text-center mb-6 relative z-10 pt-4">
        <div className="inline-flex items-center px-4 py-1.5 bg-secondary/10 dark:bg-secondary/20 text-secondary text-[9px] font-black uppercase tracking-[0.25em] rounded-full mb-6 hover:bg-secondary hover:text-white transition-all duration-300 cursor-default">
          {t(article.category || 'أقلام حرة')}
        </div>
        <h3 className="text-[14px] font-normal text-primary dark:text-white group-hover:text-secondary group-hover:text-amber-400 transition-colors leading-[1.4] line-clamp-3 min-h-[5.6rem]">
          <a href={`/article/${article.slug || article.id}`}>{t(article.title)}</a>
        </h3>
        <div className="w-12 h-1 bg-zinc-100 dark:bg-zinc-800 mx-auto mt-4 group-hover:w-20 group-hover:bg-secondary transition-all" />
      </div>

      <div className="flex-1 relative z-10 text-center">
        <p className="text-zinc-500 dark:text-zinc-400 group-hover:text-zinc-300 text-[12px] leading-relaxed line-clamp-3 font-normal not-italic mb-8 transition-colors">
          {t(article.summary)}
        </p>
      </div>

      <div className="mt-auto pt-6 flex flex-col items-center gap-4 border-t border-zinc-50 dark:border-zinc-800/10 relative z-10 text-center">
        <a href={`/author/${article.author}`} className="flex flex-col group/author">
          <span className="text-sm font-black text-primary dark:text-white group-hover:text-white group-hover/author:text-secondary transition-colors">{t(article.author)}</span>
          <span className="text-[10px] text-zinc-400 font-bold uppercase tracking-widest mt-1 transition-colors">
            {t('كاتب مقال')}
          </span>
        </a>
        
        <div className="flex items-center gap-2 text-secondary font-black text-[11px] uppercase tracking-widest opacity-0 group-hover:opacity-100 transition-all transform translate-y-2 group-hover:translate-y-0">
          {t('قراءة المقال')} {language === 'ar' ? <ArrowLeft size={14} /> : <ArrowRight size={14} />}
        </div>
      </div>
      
      <div className={cn("absolute bottom-4 opacity-[0.03] group-hover:opacity-[0.07] transition-opacity text-zinc-400 dark:text-white font-sans", language === 'ar' ? "left-4" : "right-4")}>
        <User size={80} />
      </div>
    </article>
  );
}

function OpinionCard({ article }: { article: Article }) {
  const { language, t } = useTranslate();
  return (
    <article className={cn("group relative bg-white dark:bg-zinc-900 p-8 border border-zinc-100 dark:border-zinc-800 transition-all duration-500 hover:bg-secondary dark:hover:bg-secondary hover:translate-y-[-8px] hover:shadow-[0_25px_60px_rgba(220,180,58,0.15)] h-full flex flex-col rounded-2xl overflow-hidden", language === 'ar' ? "text-right" : "text-left")}>
      <div className="absolute top-0 left-0 w-1 h-0 bg-secondary group-hover:bg-zinc-950 group-hover:h-full transition-all duration-700" />
      
      <div className="flex items-center gap-5 mb-8">
        <div className="relative">
          <div className="w-20 h-20 rounded-full overflow-hidden border-4 border-white dark:border-zinc-850 shadow-2xl group-hover:border-secondary transition-colors duration-500 relative z-10">
            <img src={getWriterAvatar(article, 150)} alt={t(article.author)} referrerPolicy="no-referrer" className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700" />
          </div>
          <div className="absolute -bottom-2 -right-2 bg-secondary text-zinc-950 p-1.5 rounded-lg shadow-xl transition-all duration-500 group-hover:bg-zinc-950 group-hover:text-secondary">
             <User size={14} />
          </div>
        </div>
        <div className="flex flex-col">
          <span className="text-[13px] font-black text-primary dark:text-white mb-1 transition-all duration-500 group-hover:text-zinc-950">{t(article.author)}</span>
          <span className="text-[10px] text-zinc-400 font-bold uppercase tracking-widest transition-all duration-500 group-hover:text-zinc-950/70">
            {t('كاتب ومحلل سياسي')}
          </span>
        </div>
      </div>

      <div className="flex-1">
        <div className="flex flex-wrap items-center gap-3 mb-6">
          <div className="px-4 py-1.5 bg-zinc-950 dark:bg-white text-white dark:text-zinc-950 text-[8px] font-black uppercase tracking-[0.2em] rounded-full shadow-md group-hover:bg-zinc-950 group-hover:text-secondary hover:bg-secondary hover:text-white dark:hover:bg-secondary dark:hover:text-white transition-all duration-500 cursor-pointer">
            {t(article.category)}
          </div>
          <div className="flex items-center gap-2 text-[10px] font-bold text-zinc-500 dark:text-zinc-400 transition-all duration-500 group-hover:text-zinc-950/80">
            <Calendar size={12} className="text-secondary opacity-70 transition-all duration-500 group-hover:text-zinc-950" />
            {formatDate(article.publishedDate)}
          </div>
        </div>

        <h3 className="text-[14px] font-normal mb-5 transition-all duration-500 text-primary dark:text-zinc-100 group-hover:text-zinc-950 group-hover:font-extrabold leading-[1.4] line-clamp-3">
          <a href={`/article/${article.slug || article.id}`}>"{t(article.title)}"</a>
        </h3>

        <p className="text-[12px] text-zinc-500 dark:text-zinc-400 mb-8 line-clamp-4 not-italic font-normal leading-relaxed transition-all duration-500 group-hover:text-zinc-950/80">
          {t(article.summary)}
        </p>
      </div>

      <div className="mt-auto pt-6 border-t border-zinc-100 dark:border-zinc-800 group-hover:border-zinc-950/20 flex items-center justify-between transition-all duration-500">
        <div className="flex items-center gap-4 text-zinc-400 text-[11px] font-bold transition-all duration-500 group-hover:text-zinc-950/80">
           <span className="flex items-center gap-1.5"><Eye size={16} className="text-zinc-300 dark:text-zinc-600 transition-all duration-500 group-hover:text-zinc-950/60" /> {article.views} {t('قراءة')}</span>
        </div>
        <span className="text-secondary transition-all duration-500 group-hover:text-zinc-950 font-black text-[12px] flex items-center gap-2 group-hover:gap-4">
          {t('اقرأ المزيد')} {language === 'ar' ? <ArrowLeft size={16} /> : <ArrowRight size={16} />}
        </span>
      </div>
    </article>
  );
}

export function ArticleCard({ article, variant, isLatestArticles }: { article: Article, variant?: string, isLatestArticles?: boolean }) {
  const { language, t } = useTranslate();
  const isSlider = variant === 'slider';
  const isSmall = variant === 'small';
  
  return (
    <article className={cn(
      "group flex flex-col h-full bg-white dark:bg-zinc-900 overflow-hidden transition-all duration-500 rounded-2xl",
      isLatestArticles 
        ? "hover:bg-secondary dark:hover:bg-secondary hover:translate-y-[-8px] shadow-sm hover:shadow-xl hover:shadow-secondary/10 border border-transparent dark:border-zinc-800/10"
        : ((isSlider || isSmall) ? "border-4 border-zinc-100 dark:border-zinc-800 p-3 hover:border-secondary/40" : "hover:translate-y-[-8px] border border-transparent dark:border-zinc-800/50 hover:border-secondary/25 hover:shadow-[0_25px_60px_rgba(0,0,0,0.08)]"),
      language === 'ar' ? "text-right" : "text-left"
    )}>
      <div className={cn(
        "relative overflow-hidden bg-zinc-100 dark:bg-zinc-800",
        "aspect-video"
      )}>
        <img 
          src={article.imageUrl} 
          alt={t(article.title)}
          className="w-full h-full object-cover grayscale-[10%] group-hover:grayscale-0 transition-all duration-1000 group-hover:scale-110"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/10 to-transparent opacity-60 group-hover:opacity-80 transition-opacity duration-500" />
        
        <div className={cn("absolute top-4", language === 'ar' ? "right-4" : "left-4", isSmall && (language === 'ar' ? "top-3 right-3" : "top-3 left-3"))}>
           <a 
             href={`/category/${article.category === 'مقالات الرأي' ? 'opinion' : article.category === 'أقلام حرة' ? 'free-pens' : article.category === 'قضايا فكرية' ? 'intellectual' : article.category === 'حوارات' ? 'dialogues' : 'news'}`}
             className={cn(
                "inline-block bg-secondary text-zinc-950 font-black px-3 py-1.5  uppercase tracking-widest shadow-xl hover:bg-white hover:text-secondary transition-all",
                isLatestArticles ? "group-hover:bg-white group-hover:text-secondary" : "",
                isSmall ? "text-[8px]" : "text-[9px]"
             )}
           >
             {t(article.category)}
           </a>
        </div>

        <div className={cn("absolute bottom-4 transform translate-y-2 opacity-0 group-hover:translate-y-0 group-hover:opacity-100 transition-all duration-500", language === 'ar' ? "right-4 left-4" : "left-4 right-4", isSmall && "bottom-3")}>
          <div className="flex items-center gap-3 text-white text-[9px] font-bold">
            <span className="flex items-center gap-1.5"><Eye size={12} className="text-secondary" /> {article.views}</span>
            <span className="flex items-center gap-1.5"><User size={12} className="text-secondary" /> {t(article.author)}</span>
          </div>
        </div>
      </div>

      <div className={cn(
        "flex flex-col flex-1 transition-colors duration-500",
        (isSlider || isSmall) ? "p-4 md:p-5 bg-white dark:bg-zinc-900" : cn("p-5 md:p-8 border border-t-0 border-zinc-100 dark:border-zinc-800", isLatestArticles ? "group-hover:border-transparent" : "group-hover:border-secondary/20"),
        isSmall && "p-4"
      )}>
        <div className={cn(
          "flex items-center gap-3 text-secondary mb-3 uppercase tracking-[0.15em] font-black transition-colors duration-500",
          isLatestArticles ? "group-hover:text-zinc-950" : "text-secondary",
          isSmall ? "text-[8px] mb-2" : "text-[10px]"
        )}>
          <Calendar size={isSmall ? 10 : 12} className={cn("transition-colors duration-500", isLatestArticles && "group-hover:text-zinc-950")} />
          {formatDate(article.publishedDate)}
        </div>
        
        <h3 className={cn(
          "text-[14px] font-normal mb-4 transition-colors duration-500 text-primary dark:text-zinc-100 leading-[1.3] line-clamp-2",
          isLatestArticles ? "group-hover:text-zinc-950 group-hover:font-extrabold" : "group-hover:text-secondary"
        )}>
          <a href={`/article/${article.slug || article.id}`}>{t(article.title)}</a>
        </h3>
        
        {!isSmall && (
          <div className="transition-all duration-500 max-h-0 opacity-0 overflow-hidden group-hover:max-h-24 group-hover:opacity-100 group-hover:mb-6">
            <p className={cn(
              "line-clamp-3 leading-relaxed font-normal text-[12px] not-italic transition-colors duration-500",
              isLatestArticles ? "text-zinc-500 dark:text-zinc-400 group-hover:text-zinc-950/80" : "text-zinc-500 dark:text-zinc-400"
            )}>
              {t(article.summary)}
            </p>
          </div>
        )}

        <div className={cn(
          "mt-auto flex items-center justify-between border-t transition-colors duration-500",
          isLatestArticles ? "border-zinc-50 dark:border-zinc-850 group-hover:border-zinc-950/20" : "border-zinc-50 dark:border-zinc-850",
          isSmall ? "pt-3 mt-1" : "pt-5 mt-2"
        )}>
          <div className="flex items-center gap-2">
            <div className={cn(
               "bg-zinc-100 dark:bg-zinc-800 border overflow-hidden transition-all duration-500 rounded-full p-0.5 shadow-md",
               isLatestArticles ? "border-zinc-200 dark:border-zinc-700 group-hover:border-zinc-950/40 group-hover:shadow-lg" : "border-zinc-200 dark:border-zinc-700 group-hover:border-secondary/50",
               isSmall ? "w-8 h-8" : "w-12 h-12"
            )}>
              <img src={getWriterAvatar(article, 150)} alt={t(article.author)} referrerPolicy="no-referrer" className="w-full h-full object-cover rounded-full group-hover:scale-110 transition-transform duration-700" />
            </div>
            <span className={cn(
              "font-black transition-colors duration-500",
              isLatestArticles ? "text-zinc-900 dark:text-zinc-100 group-hover:text-zinc-950" : "text-zinc-900 dark:text-zinc-100",
              isSmall ? "text-[9px]" : "text-[11px]"
            )}>{t(article.author)}</span>
          </div>
          <div className={cn(
            "rounded-full border flex items-center justify-center transition-all transform duration-500",
            isLatestArticles 
              ? "border-zinc-100 dark:border-zinc-800 text-primary dark:text-white group-hover:bg-zinc-950 group-hover:text-secondary group-hover:border-zinc-950"
              : "border-zinc-100 dark:border-zinc-800 text-primary dark:text-white group-hover:bg-secondary group-hover:text-white group-hover:border-secondary",
            isSmall ? "w-8 h-8" : "w-10 h-10",
            language === 'ar' ? "group-hover:-translate-x-1" : "group-hover:translate-x-1"
          )}>
            {language === 'ar' ? <ArrowLeft size={isSmall ? 12 : 16} /> : <ArrowRight size={isSmall ? 12 : 16} />}
          </div>
        </div>
      </div>
    </article>
  );
}
